class MembershipPolicyTooltip extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.content = this.querySelector('[role="tooltip"]');

    if (!this.button || !this.content) return;

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);

    this.button.addEventListener('click', this.onButtonClick);
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.button.addEventListener('focus', this.onFocus);
    this.button.addEventListener('blur', this.onBlur);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('scroll', this.onViewportChange, true);
    window.addEventListener('resize', this.onViewportChange);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onButtonClick);
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.button?.removeEventListener('focus', this.onFocus);
    this.button?.removeEventListener('blur', this.onBlur);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('scroll', this.onViewportChange, true);
    window.removeEventListener('resize', this.onViewportChange);
    cancelAnimationFrame(this.positionFrame);
  }

  onButtonClick(event) {
    event.stopPropagation();
    this.hasAttribute('open') ? this.close() : this.open();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.hasAttribute('open')) {
      this.close();
      this.button.focus();
    }
  }

  onMouseEnter() {
    this.open();
  }

  onMouseLeave() {
    if (document.activeElement !== this.button) this.close();
  }

  onFocus() {
    this.open();
  }

  onBlur() {
    this.close();
  }

  onViewportChange() {
    if (this.hasAttribute('open')) this.positionTooltip();
  }

  open() {
    document.querySelectorAll('membership-policy-tooltip[open]').forEach((tooltip) => {
      if (tooltip !== this) tooltip.close();
    });

    this.setAttribute('open', '');
    this.button.setAttribute('aria-expanded', 'true');
    this.content.hidden = false;
    this.positionTooltip();
    cancelAnimationFrame(this.positionFrame);
    this.positionFrame = requestAnimationFrame(() => this.positionTooltip());
  }

  close() {
    if (!this.button || !this.content) return;

    this.removeAttribute('open');
    this.button.setAttribute('aria-expanded', 'false');
    this.content.hidden = true;
    this.content.style.removeProperty('--membership-policy-tooltip-top');
    this.content.style.removeProperty('--membership-policy-tooltip-left');
  }

  positionTooltip() {
    if (!this.button || !this.content || this.content.hidden) return;

    const viewportPadding = 12;
    const gap = 8;
    const buttonRect = this.button.getBoundingClientRect();
    const tooltipRect = this.content.getBoundingClientRect();
    const maxWidth = window.innerWidth - viewportPadding * 2;
    const tooltipWidth = Math.min(tooltipRect.width, maxWidth);
    const tooltipHeight = tooltipRect.height;
    const centeredLeft = buttonRect.left + buttonRect.width / 2 - tooltipWidth / 2;
    const left = Math.max(viewportPadding, Math.min(centeredLeft, window.innerWidth - tooltipWidth - viewportPadding));
    const tierHeader = this.closest('thead th');
    const belowTop = tierHeader ? tierHeader.getBoundingClientRect().bottom + 18 : buttonRect.bottom + gap;
    const top = belowTop + tooltipHeight <= window.innerHeight - viewportPadding
      ? belowTop
      : Math.max(viewportPadding, buttonRect.top - tooltipHeight - gap);

    this.content.style.setProperty('--membership-policy-tooltip-top', `${Math.round(top)}px`);
    this.content.style.setProperty('--membership-policy-tooltip-left', `${Math.round(left)}px`);
  }
}

if (!customElements.get('membership-policy-tooltip')) {
  customElements.define('membership-policy-tooltip', MembershipPolicyTooltip);
}

class MembershipPolicyTableScrollShadow {
  constructor(element) {
    this.element = element;
    this.sync = this.sync.bind(this);

    this.element.addEventListener('scroll', this.sync, { passive: true });
    window.addEventListener('resize', this.sync);
    this.sync();
  }

  sync() {
    const isScrollable = this.element.scrollWidth - this.element.clientWidth > 1;
    this.element.toggleAttribute('data-scrolled', isScrollable && this.element.scrollLeft > 1);
  }
}

function initializeMembershipPolicyTableScrollShadows(root = document) {
  root.querySelectorAll('.membership-policy__table-scroll').forEach((element) => {
    if (element.dataset.scrollShadowInitialized) return;

    element.dataset.scrollShadowInitialized = 'true';
    new MembershipPolicyTableScrollShadow(element);
  });
}

initializeMembershipPolicyTableScrollShadows();
document.addEventListener('shopify:section:load', (event) => {
  initializeMembershipPolicyTableScrollShadows(event.target);
});
